import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  useClearAdLaunchGoalMutation,
  useSetAdLaunchGoalMutation,
} from "~/ui-rtk/api/adBlastReportsApi"
import { Input } from "~/ui-rtk/components/ui/controls"
import useDate from "~/ui-rtk/hooks/date"
import {
  type Aggregate,
  type Comparison,
  type CustomerGoalStatus,
  formatPercent,
} from "./aggregate"
import {
  AdNameRef,
  CompareValue,
  DefinitionList,
  ExternalLink,
  KpiRow,
  MetaStrip,
  Person,
  PostRef,
  SectionBand,
  SheetTable,
  StatusPill,
  type Tone,
  typeLabel,
} from "./sheet"

const DEFINITIONS = [
  {
    term: "Launched",
    text: "One ad created by a Marathon ad blast into one ad set (one line per ad, never merged). Failed attempts are not reported.",
  },
  {
    term: "Live",
    text: "A launched ad that is currently ACTIVE (synced entity status; falls back to the status Meta returned at creation).",
  },
  {
    term: "Windows",
    text: "Relative to the as-of date (range end, capped at now) in your browser timezone; weeks are Monday–Sunday.",
  },
  {
    term: "Daily goal",
    text: "The weekly low goal spread over the five weekdays (ceil(low ÷ 5) Mon–Fri, 0 on weekends).",
  },
  {
    term: "Caveat",
    text: "Only blasts run through Marathon are counted — ads created directly in Ads Manager are not in the audit history.",
  },
]

const customerTone = (customer: CustomerGoalStatus): Tone => {
  if (!customer.hasGoal) return "muted"
  if (customer.goalStatus === "over goal") return "good"
  if (customer.delta < 0) return "bad"
  return "default"
}

/**
 * Inline low/high goal editor for one customer row. Saves on blur or Enter;
 * both fields blank removes the goal (the company leaves the tracker unless
 * it still has launches in the window).
 */
function GoalEditor({ customer }: { customer: CustomerGoalStatus }) {
  const [low, setLow] = useState(
    customer.hasGoal ? String(customer.goalLow) : "",
  )
  const [high, setHigh] = useState(
    customer.hasGoal ? String(customer.goalHigh) : "",
  )
  const [setGoal, { isLoading: isSaving }] = useSetAdLaunchGoalMutation()
  const [clearGoal, { isLoading: isClearing }] = useClearAdLaunchGoalMutation()

  useEffect(() => {
    setLow(customer.hasGoal ? String(customer.goalLow) : "")
    setHigh(customer.hasGoal ? String(customer.goalHigh) : "")
  }, [customer.hasGoal, customer.goalLow, customer.goalHigh])

  const commit = async () => {
    const lowValue = low.trim() === "" ? null : Number(low)
    const highValue = high.trim() === "" ? lowValue : Number(high)
    if (lowValue === null && highValue === null) {
      if (!customer.hasGoal) return
      try {
        await clearGoal(customer.companyId).unwrap()
        toast.success(`Goal removed for ${customer.companyName}`)
      } catch {
        toast.error("Couldn't remove the goal")
      }
      return
    }
    if (
      lowValue === null ||
      highValue === null ||
      !Number.isInteger(lowValue) ||
      !Number.isInteger(highValue) ||
      lowValue < 0 ||
      highValue < lowValue
    ) {
      toast.error("Goal must be whole numbers with high ≥ low")
      return
    }
    if (
      customer.hasGoal &&
      lowValue === customer.goalLow &&
      highValue === customer.goalHigh
    ) {
      return
    }
    try {
      await setGoal({
        companyId: customer.companyId,
        low: lowValue,
        high: highValue,
      }).unwrap()
      toast.success(`Goal saved for ${customer.companyName}`)
    } catch (e) {
      const message = (e as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Couldn't save the goal")
    }
  }

  const goalInputClasses = {
    className: "rounded px-1 py-0.5",
    classes: {
      input: "text-right text-[12px] tabular-nums disabled:opacity-60",
    },
  }
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur()
  }
  const busy = isSaving || isClearing
  /**
   * Save once when focus leaves the editor as a whole — tabbing from low to
   * high must not fire an interim save with a half-edited pair.
   */
  const handleBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit()
  }
  return (
    <span className="inline-flex items-center gap-1" onBlur={handleBlur}>
      <Input
        {...goalInputClasses}
        width={52}
        aria-label={`${customer.companyName} goal low`}
        inputMode="numeric"
        value={low}
        disabled={busy}
        onChange={e => setLow(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="—"
      />
      <span className="text-system-gray-500">/</span>
      <Input
        {...goalInputClasses}
        width={52}
        aria-label={`${customer.companyName} goal high`}
        inputMode="numeric"
        value={high}
        disabled={busy}
        onChange={e => setHigh(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="—"
      />
    </span>
  )
}

/** The "Aggregate" sheet — mirrors the agency's Aggregate tab block for block. */
export default function AggregateSheet({
  aggregate,
  generatedAt,
  timezone,
  comparison,
}: {
  aggregate: Aggregate
  generatedAt: string
  timezone: string
  /** Present when the date picker has a comparison period enabled. */
  comparison?: { from: Date; to: Date; data: Comparison }
}) {
  const dayjs = useDate()
  const { snapshot, windows, windowCounts, customers } = aggregate
  const fmtDate = (d: Date | string | null) =>
    d ? dayjs(d).format("YYYY-MM-DD") : ""
  const fmtDateTime = (d: Date | string) => dayjs(d).format("YYYY-MM-DD HH:mm")
  const counts = (c: { launched: number; live: number }) =>
    `${c.launched} / ${c.live}`
  // Comparison-period readings, keyed by customer (undefined = no compare).
  const previousByCompany = new Map(
    comparison?.data.rows.map(r => [r.companyId, r.previous]) ?? [],
  )
  const previousTotals = comparison?.data.totals.previous
  /** "launched / live" with the comparison reading underneath (live drives the trend). */
  const compareCounts = (
    current: { launched: number; live: number },
    previous?: { launched: number; live: number },
  ) => (
    <CompareValue
      value={current.live}
      previous={previous?.live}
      label={counts(current)}
    />
  )

  return (
    <div className="flex flex-col">
      <div className="rounded-t-lg border border-system-blue-600/40 bg-system-blue-700 px-3 py-2 text-[18px] font-bold text-white">
        Marathon Engine Ads Launch Tracker - Aggregate
      </div>
      <MetaStrip
        items={[
          { label: "Last refreshed", value: fmtDateTime(generatedAt) },
          { label: "Timezone", value: timezone },
          { label: "As of", value: fmtDateTime(aggregate.asOf) },
          ...(comparison
            ? [
                {
                  label: "Compared to",
                  value: `${fmtDate(comparison.from)} – ${fmtDate(comparison.to)}`,
                },
              ]
            : []),
        ]}
      />

      <SectionBand title="5-second snapshot" />
      <KpiRow
        items={[
          { label: "Yesterday launch goal", value: snapshot.yesterdayGoal },
          { label: "Yesterday live", value: snapshot.yesterdayLive },
          { label: "Today launch goal", value: snapshot.todayGoal },
          { label: "Today so far live", value: snapshot.todayLive },
          {
            label: "This week launched / live",
            value: `${snapshot.thisWeekLaunched} / ${snapshot.thisWeekLive}`,
          },
          {
            label: "Weekly attainment",
            value: formatPercent(snapshot.weeklyAttainment),
          },
          {
            label: "Customers on goal",
            value: `${snapshot.customersOnGoal} / ${snapshot.customersTracked}`,
          },
          { label: "Last week live", value: snapshot.lastWeekLive },
          { label: "Last week launch goal", value: snapshot.lastWeekGoal },
          {
            label: "Last week attainment",
            value: formatPercent(snapshot.lastWeekAttainment),
          },
          {
            label: "Last week customers on goal",
            value: `${snapshot.lastWeekCustomersOnGoal} / ${snapshot.customersTracked}`,
          },
        ]}
      />

      <div className="grid grid-cols-1 border border-t-0 border-system-blue-600/40 text-[13px] md:grid-cols-2">
        <div className="flex gap-3 bg-amber-500/10 px-2 py-1.5 text-amber-200">
          <span className="w-32 shrink-0 font-bold">Needs attention</span>
          <span>
            {aggregate.needsAttention.length === 0
              ? "None"
              : aggregate.needsAttention
                  .map(c => `${c.companyName} ${c.delta}`)
                  .join("; ")}
          </span>
        </div>
        <div className="flex gap-3 bg-emerald-500/10 px-2 py-1.5 text-emerald-200">
          <span className="w-32 shrink-0 font-bold">Over goal</span>
          <span>
            {aggregate.overGoal.length === 0
              ? "None yet"
              : aggregate.overGoal
                  .map(c => `${c.companyName} +${c.delta}`)
                  .join("; ")}
          </span>
        </div>
      </div>

      <SectionBand title="Definitions" />
      <DefinitionList items={DEFINITIONS} />

      <SectionBand title="Window summary" />
      <SheetTable
        rows={windows}
        rowKey={w => w.key}
        maxHeight="max-h-none"
        columns={[
          { header: "Window", render: w => w.label },
          {
            header: "Start",
            render: w => fmtDateTime(w.start),
            align: "right",
          },
          { header: "End", render: w => fmtDateTime(w.end), align: "right" },
          {
            header: "Launched",
            render: w => (
              <CompareValue
                value={windowCounts[w.key].launched}
                previous={
                  w.key === "range" ? previousTotals?.launched : undefined
                }
              />
            ),
            align: "right",
          },
          {
            header: "Live",
            render: w => (
              <CompareValue
                value={windowCounts[w.key].live}
                previous={w.key === "range" ? previousTotals?.live : undefined}
              />
            ),
            align: "right",
          },
        ]}
      />
      <MetaStrip
        items={[
          {
            label: "Weekly goal summary — customers on goal",
            value: snapshot.customersOnGoal,
          },
          { label: "Customers tracked", value: snapshot.customersTracked },
          {
            label: "Live vs min goal",
            value: `${snapshot.thisWeekLive} / ${snapshot.weeklyGoal}`,
          },
          {
            label: "Attainment",
            value: formatPercent(snapshot.weeklyAttainment),
          },
        ]}
      />

      <SectionBand title="Customer goal status">
        <span className="text-[11px] font-normal text-system-gray-400">
          Edit goals inline (low / high, saved on blur; blank both to remove)
        </span>
      </SectionBand>
      <SheetTable
        rows={customers}
        rowKey={c => c.companyId}
        tone={customerTone}
        emptyLabel="No customers in this window — set a goal below or widen the range."
        columns={[
          {
            header: "Customer",
            render: c => <span className="font-medium">{c.companyName}</span>,
          },
          {
            header: "Goal low / high",
            render: c => <GoalEditor key={c.companyId} customer={c} />,
            align: "center",
          },
          {
            header: "Yesterday launched / live",
            render: c => counts(c.yesterday),
            align: "right",
          },
          {
            header: "Today launched / live",
            render: c => counts(c.today),
            align: "right",
          },
          {
            header: "This week launched / live",
            render: c => counts(c.thisWeek),
            align: "right",
          },
          {
            header: "Last week live",
            render: c => c.lastWeek.live,
            align: "right",
          },
          {
            header: "Range launched / live",
            render: c =>
              compareCounts(
                c.range,
                comparison
                  ? (previousByCompany.get(c.companyId) ?? {
                      launched: 0,
                      live: 0,
                    })
                  : undefined,
              ),
            align: "right",
          },
          {
            header: "Attainment %",
            render: c => formatPercent(c.attainment),
            align: "right",
          },
          { header: "Goal status", render: c => c.goalStatus },
          {
            header: "Channels this week",
            render: c => c.channelsThisWeek.join(", "),
          },
          {
            header: "Latest launch date",
            render: c => fmtDate(c.latestLaunchAt),
            align: "right",
          },
        ]}
      />

      <SectionBand title="This week by customer/channel" />
      <SheetTable
        rows={aggregate.byCustomerChannel}
        rowKey={r => `${r.companyId}|${r.channel}`}
        maxHeight="max-h-[40vh]"
        emptyLabel="No launches this week."
        columns={[
          { header: "Customer", render: r => r.companyName },
          { header: "Channel", render: r => r.channel },
          {
            header: "This week launched",
            render: r => r.launched,
            align: "right",
          },
          { header: "This week live", render: r => r.live, align: "right" },
        ]}
      />

      <SectionBand title="Recent launches (selected range)" />
      <SheetTable
        rows={aggregate.recentLaunches.slice(0, 100)}
        rowKey={l => l.id}
        dense
        emptyLabel="No blasts in the selected range."
        columns={[
          {
            header: "Date",
            render: l => fmtDateTime(l.launchedAt),
            align: "right",
          },
          { header: "Customer", render: l => l.companyName },
          { header: "By", render: l => <Person value={l.createdBy} /> },
          { header: "Status", render: l => <StatusPill status={l.status} /> },
          { header: "Channel", render: l => l.channel },
          { header: "Type", render: l => typeLabel(l.type) },
          { header: "Post", render: l => <PostRef postId={l.postId} /> },
          {
            header: "Campaign // Ad sets",
            render: l => l.campaignAdSets,
            className: "min-w-[300px] max-w-[460px] break-words",
          },
          {
            header: "Ad name",
            render: l => (
              <AdNameRef name={l.adName} previewUrl={l.previewUrl} />
            ),
            className: "min-w-[220px] max-w-[340px] break-words",
          },
          {
            header: "Preview",
            render: l => (
              <ExternalLink href={l.previewUrl} label="Ads Manager" />
            ),
          },
        ]}
      />
    </div>
  )
}
